import { GetAccount200Response } from "../../../codegen-api";
import { getNativeSymbol } from "../../../config/chains";
import { AllCollaterals } from "../../../constants/addresses";
import { getPercent } from "../../../utils/math";
import { ICollateralRow } from "./Token.types";

export function computeCollaterals(
  accountData?: GetAccount200Response
): ICollateralRow[] {
  const totalBalance = Number(
    accountData?.collaterals?.reduce(
      (prev, collat) => prev + Number(collat.collateral_value || 0),
      0
    ) || 0
  );

  // Map backend asset names to display names. WETH is the wrapped-ETH ERC20
  // and stays "ETH" on every chain — including chains where the gas token is
  // not ETH (e.g. Arc, where native is USDC). Only WNATIVE follows the chain's
  // native symbol.
  const nativeSym = getNativeSymbol();
  const assetNameMap: Record<string, string> = {
    MUSDC: "USDC",
    WBTC: "BTC",
    mWBTC: "BTC",
    WETH: "ETH",
    WNATIVE: nativeSym,
    "Wrapped Native": nativeSym,
    "Wrapped Native USDC": nativeSym,
    "USDC (Native Wrapped)": nativeSym,
  };

  // Default tokens to always show (spot pool tokens). USDC, BTC, and ETH are
  // the protocol's canonical base/quote assets — keep them stable across
  // chains so a chain with `nativeSym=USDC` (Arc) doesn't produce a duplicate
  // USDC row instead of an ETH row.
  const defaultTokens = ["USDC", "BTC", "ETH"];
  // Map the DISPLAY name (BTC/ETH/USDC) back to the canonical collateral enum
  // (WBTC/WETH/USDC) used by the deposit/withdraw allow-lists. Without this the
  // row's id is "BTC", which doesn't match "WBTC", so the modal falls back to USDC.
  const displayToCollateralEnum: Record<string, string> = {
    BTC: "WBTC",
    ETH: "WETH",
    USDC: "USDC",
  };
  const seen = new Set<string>();

  const rowsByAsset = new Map<string, ICollateralRow>();

  (accountData?.collaterals || []).forEach((c) => {
    const asset = c.collateral_asset as any;
    const rawName = asset?.asset_id || asset?.asset_name || asset || "USDC";
    const displayName = assetNameMap[rawName] || rawName;
    const collateralEnum = (displayToCollateralEnum[displayName] ||
      displayName) as AllCollaterals;
    const balance = Number(c.balance || 0);
    const value = Number(c.collateral_value || 0);
    const existing = rowsByAsset.get(displayName);

    seen.add(displayName);

    rowsByAsset.set(displayName, {
      id: (existing?.id || collateralEnum) as AllCollaterals,
      asset: (existing?.asset || displayName) as AllCollaterals,
      balance: (existing?.balance || 0) + balance,
      value: (existing?.value || 0) + value,
      composition:
        totalBalance
          ? getPercent((existing?.value || 0) + value, totalBalance) * 100
          : 0,
      unrealizedPnl:
        (existing?.unrealizedPnl || 0) + Number(c.unrealized_pnl || "0"),
    });
  });

  const fromApi = Array.from(rowsByAsset.values());

  // Default tokens the API did not return.
  //
  // Whether that means ZERO or UNKNOWN depends entirely on whether we got an
  // account back at all:
  //   - `accountData` present, token absent from `collaterals` -> the account
  //     really holds none of it. A real zero.
  //   - `accountData` undefined -- disconnected, still loading, or /account
  //     401/500'd -- we were never told anything. Filling these in at zero made
  //     the table assert "USDC 0.00 / $0.00 / 0.00%" for balances nobody has
  //     stated, on the same screen that correctly renders Equity as a dash.
  const accountKnown = accountData !== undefined;
  const missing = defaultTokens
    .filter((t) => !seen.has(t))
    .map((t) => ({
      id: (displayToCollateralEnum[t] || t) as AllCollaterals,
      asset: t as AllCollaterals,
      balance: accountKnown ? 0 : undefined,
      value: accountKnown ? 0 : undefined,
      composition: accountKnown ? 0 : undefined,
      unrealizedPnl: accountKnown ? 0 : undefined,
    }));

  // Sort by a stable key so rows don't reshuffle (and re-load icons /
  // shift layout) when the account API returns collaterals in a
  // different order on each poll. Keep the canonical USDC > BTC > ETH
  // priority from `defaultTokens`, then fall back to alphabetical.
  const priority = new Map(defaultTokens.map((t, i) => [t, i]));
  return [...fromApi, ...missing].sort((a, b) => {
    const ap = priority.get(String(a.asset)) ?? defaultTokens.length;
    const bp = priority.get(String(b.asset)) ?? defaultTokens.length;
    if (ap !== bp) return ap - bp;
    return String(a.asset).localeCompare(String(b.asset));
  });
}
